import React, { useEffect, useState } from "react";

import { Button, Col, Modal, Row } from "react-bootstrap";

import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { ReactSVG } from "react-svg";

import {
  type GetBeneficiaryViewQuery,
  type Participant,
  type RecipientRelationshipInput,
  type ShellParticipant,
  useBatchUpsertRecipientRelationshipMutation,
  useCreateShellBeneficiariesMutation,
} from "@api/graphql";
import { CrossIcon, PlusIcon } from "@assets/icons";
import { ToastMessage } from "@components/ToastMessage";

import {
  BeneficiaryType,
  ExtendedRecipientRelationshipInput,
  ParticipantSearchForm,
  PercentageShareForm,
  StartDateComponent,
  TotalPercentageDisplay,
} from "./BeneficiaryModalForm";
import { BeneficiaryModalHeader } from "./BeneficiaryModalHeader";
import { BeneficiarySuccessModel } from "./BeneficiarySuccessModel";

import styles from "./AddBeneficiaryModal.module.scss";

export type Relation = GetBeneficiaryViewQuery["getBeneficiaryView"]["relations"][number];

interface AddBeneficiaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant: Participant;
  relation: Relation;
  refetchBeneficiary: () => void;
}

export const AddBeneficiaryModal = ({
  isOpen,
  onClose,
  participant,
  relation,
  refetchBeneficiary,
}: AddBeneficiaryModalProps) => {
  const rootArtist = relation.root_artist;
  const beneficiary = relation.beneficiary;
  const percentageShare = beneficiary?.attributes?.overall_royalty_percentage;

  const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);
  const [selectedParticipants, setSelectedParticipants] = useState<{ [key: number]: Participant }>({});
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [addBeneficiaryMutation] = useBatchUpsertRecipientRelationshipMutation({});
  const [createShellBeneficiaries] = useCreateShellBeneficiariesMutation({});
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  const form = useForm<{ start_date: string; relationships: RecipientRelationshipInput[] }>();

  const { control } = form;

  const { fields, append, update, replace, remove } = useFieldArray({
    control,
    name: "relationships",
    keyName: "_id",
  });

  const relationships = useWatch({
    control,
    name: "relationships",
  });

  useEffect(() => {
    if (relationships) {
      const total = relationships.reduce((acc, curr) => {
        const percentage = curr.percentage_share > 0 ? curr.percentage_share : 0;
        return acc + percentage;
      }, 0);
      setTotalPercentage(total);
      setIsConfirmDisabled(total !== 100);
    }
  }, [relationships]);

  useEffect(() => {
    if (isOpen) {
      const initialRelationships: RecipientRelationshipInput[] = [];
      const initialSelectedParticipants: { [key: number]: Participant } = {};
      relation.recipients.forEach((recipient, index) => {
        const beneficiary_id = beneficiary.node_id;
        const percentage = recipient.attributes.percentages.find((p) => p.parent === beneficiary_id);

        if (percentage) {
          const relationship: RecipientRelationshipInput = {
            id: percentage.recipient_relationship_id,
            is_art_centre_or_cmo: false,
            percentage_share: percentage.percentage,
            recipient_id: recipient.node_id,
            root_artist_id: rootArtist.node_id,
            conferrer_id: participant.participant_id,
          };

          initialRelationships.push(relationship);

          initialSelectedParticipants[index] = {
            participant_id: recipient.node_id,
            full_name: recipient.attributes.name,
          } as Participant;
        }
      });

      replace(initialRelationships);
      setSelectedParticipants(initialSelectedParticipants);

      const startDate = relation.recipients?.[0]?.attributes.start_date || "";
      if (startDate) {
        form.setValue("start_date", startDate);
      }
    }
  }, [beneficiary, relation, rootArtist, participant, replace, form.setValue, form, isOpen]);

  const handleAddNew = () => {
    append({
      is_art_centre_or_cmo: false,
      percentage_share: 0,
      recipient_id: 0,
      root_artist_id: rootArtist.node_id || 0,
      conferrer_id: participant?.participant_id || 0,
    });
  };

  const handleDelete = (index: number) => {
    if (!fields[index].id) {
      remove(index);
      setSelectedParticipants((prev) => {
        const updatedParticipants = { ...prev };
        delete updatedParticipants[index];
        return updatedParticipants;
      });
    }
  };

  const handleParticipantChange = (index: number, selectedParticipant: Participant | undefined) => {
    if (selectedParticipant) {
      // Check for duplicate selection
      if (Object.values(selectedParticipants).some((p) => p.participant_id === selectedParticipant.participant_id)) {
        return; // Prevent duplicate participant selection
      }

      update(index, {
        ...fields[index],
        recipient_id: selectedParticipant.participant_id > 0 ? selectedParticipant.participant_id : -index,
      });
      setSelectedParticipants((prev) => ({
        ...prev,
        [index]: {
          ...selectedParticipant,
          participant_id: selectedParticipant.participant_id > 0 ? selectedParticipant.participant_id : -index,
        },
      }));
    } else {
      // Handle participant removal
      update(index, {
        ...fields[index],
        recipient_id: 0,
      });

      setSelectedParticipants((prev) => {
        const newParticipants = { ...prev };
        delete newParticipants[index];
        return newParticipants;
      });
    }
  };

  const selectedParticipantIds = new Set(Object.values(selectedParticipants).map((p) => p.participant_id));

  const createShellParticipants = async (nonParticipantRecipients: string[] | undefined) => {
    if (nonParticipantRecipients === undefined || !nonParticipantRecipients?.length) {
      return {};
    } else {
      const shellBeneficiariesResponse = (
        await createShellBeneficiaries({
          variables: {
            names: nonParticipantRecipients,
          },
        })
      ).data?.createShellBeneficiaries;

      if (shellBeneficiariesResponse !== undefined) {
        let mappedShellBeneficaryReturn: Record<string, ShellParticipant> = {};
        for (const shell of shellBeneficiariesResponse) {
          const keyName = nonParticipantRecipients.find((participant) => participant === shell.full_name);
          if (keyName) {
            mappedShellBeneficaryReturn = { ...mappedShellBeneficaryReturn, [keyName]: { ...shell } };
          }
        }

        return mappedShellBeneficaryReturn;
      }
    }
  };

  const onSubmit = async (data: { start_date: string; relationships: ExtendedRecipientRelationshipInput[] }) => {
    const { start_date, relationships } = data;

    if (totalPercentage !== 100) {
      setToastMessage("Total percentage must be 100%");
      setShowToast(true);
      return;
    }

    const nonParticipantRecipients: string[] | undefined = relationships
      .filter((relationship) => relationship.beneficiary_type === BeneficiaryType.Shell)
      .map((relationship) => relationship.shell_beneficiary_name)
      .filter((name): name is string => name !== undefined);

    const shells = await createShellParticipants(nonParticipantRecipients);

    const relationshipsWithStartDate: RecipientRelationshipInput[] = relationships.map((relationship) => ({
      id: relationship.id,
      is_art_centre_or_cmo: relationship.is_art_centre_or_cmo,
      conferrer_id: relationship.conferrer_id,
      percentage_share: relationship.percentage_share,
      root_artist_id: relationship.root_artist_id,
      start_date,
      recipient_id:
        relationship.beneficiary_type === BeneficiaryType.Shell && relationship.shell_beneficiary_name !== undefined
          ? shells?.[relationship.shell_beneficiary_name]?.participant_id ?? relationship.recipient_id
          : relationship.recipient_id,
    }));

    try {
      const response = await addBeneficiaryMutation({
        variables: {
          relationships: relationshipsWithStartDate,
        },
      });
      const upsertResponses = response.data?.batchUpsertRecipientRelationship?.recipient_upsert_responses;
      if (upsertResponses?.every((res) => res.success)) {
        onClose();
        setIsSuccessModalOpen(true);
        refetchBeneficiary();
      } else {
        const failedMessages = upsertResponses
          ?.filter((res) => !res.success)
          .map((res) => res.message)
          .join(", ");
        setToastMessage(`Error adding beneficiary details: ${failedMessages}`);
        setShowToast(true);
      }
    } catch (error) {
      if (error instanceof Error) {
        setToastMessage(`Error adding beneficiary details: ${error.message}`);
      } else {
        setToastMessage("An unknown error occurred.");
      }
      setShowToast(true);
    }
  };

  return (
    <>
      <ToastMessage message={toastMessage} show={showToast} setShow={setShowToast} type="error" />
      <Modal show={isOpen} size="lg" onHide={onClose}>
        <Modal.Header closeButton />
        <Modal.Body className="p-5">
          <BeneficiaryModalHeader
            rootArtistName={rootArtist?.attributes.name}
            percentageShare={percentageShare}
            participantName={participant?.full_name}
          />
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <StartDateComponent />
              <Row className="mt-4 mb-4">
                <Button variant="none" className={styles["add-new-button"]} onClick={handleAddNew}>
                  <ReactSVG src={PlusIcon} />
                  <span>Add new</span>
                </Button>
              </Row>
              {fields.length > 0 && (
                <>
                  <TotalPercentageDisplay totalPercentage={totalPercentage} />

                  {fields.map((item, index) => (
                    <Row className="mb-4" key={item._id}>
                      <ParticipantSearchForm
                        index={index}
                        handleParticipantChange={handleParticipantChange}
                        selectedParticipants={selectedParticipants}
                        selectedParticipantIds={selectedParticipantIds}
                        currentParticipantTypes={participant.participant_types}
                      />
                      <PercentageShareForm
                        item={item}
                        index={index}
                        recipient_percentage_share={relationships?.[index]?.percentage_share}
                        conferrer_share={percentageShare}
                      />
                      {!item.id && (
                        <Col xs="1" className="d-flex align-items-center">
                          <Button variant="plain" onClick={() => handleDelete(index)}>
                            <ReactSVG src={CrossIcon} />
                          </Button>
                        </Col>
                      )}
                    </Row>
                  ))}
                </>
              )}
              <Row className="mt-4 mb-4 text-center">
                <Col>
                  <Button type="submit" disabled={isConfirmDisabled}>
                    Confirm
                  </Button>
                </Col>
              </Row>
            </form>
          </FormProvider>
        </Modal.Body>
        <Modal.Footer className={styles["footer-note"]}>
          <Row>Note that updating these recipients will not change in progress payments.</Row>
        </Modal.Footer>
      </Modal>
      <BeneficiarySuccessModel
        isOpen={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
        }}
        name={beneficiary?.attributes.name}
      />
    </>
  );
};
